// src/lib/currency.js
// ─────────────────────────────────────────────────────────────
// CRITICAL SAFETY RULE:
// All prices are stored and charged in EUR.
// Display currencies (HUF, PLN, RON) are COSMETIC ONLY.
// Mollie always receives the EUR amount.
// Checkout always shows and confirms the EUR amount.
// This file must NEVER be used to calculate payment amounts.
// ─────────────────────────────────────────────────────────────

export const COUNTRY_CONFIG = {
  DE: { currency: 'EUR', lang: 'de', flag: '🇩🇪', name: 'Germany',     shipping_free_eur: 60 },
  SK: { currency: 'EUR', lang: 'sk', flag: '🇸🇰', name: 'Slovakia',    shipping_free_eur: 60 },
  HU: { currency: 'HUF', lang: 'hu', flag: '🇭🇺', name: 'Hungary',     shipping_free_eur: 60 },
  RO: { currency: 'RON', lang: 'ro', flag: '🇷🇴', name: 'Romania',     shipping_free_eur: 60 },
  PL: { currency: 'PLN', lang: 'pl', flag: '🇵🇱', name: 'Poland',      shipping_free_eur: 60 },
  AT: { currency: 'EUR', lang: 'de', flag: '🇦🇹', name: 'Austria',     shipping_free_eur: 60 },
  NL: { currency: 'EUR', lang: 'nl', flag: '🇳🇱', name: 'Netherlands', shipping_free_eur: 60 },
  HR: { currency: 'EUR', lang: 'hr', flag: '🇭🇷', name: 'Croatia',     shipping_free_eur: 60 },
};

// Display exchange rates — approximate, for display only
// These are NOT used for payment. Mollie receives EUR.
const DISPLAY_RATES = {
  EUR: 1,
  HUF: 390,
  PLN: 4.28,
  RON: 4.97,
};

const CURRENCY_SYMBOLS = {
  EUR: '€',
  HUF: 'Ft',
  PLN: 'zł',
  RON: 'lei',
};

const CURRENCY_DECIMALS = {
  EUR: 2,
  HUF: 0,
  PLN: 2,
  RON: 2,
};

/**
 * Format a EUR price for DISPLAY only in the user's local currency.
 * Always appends "(€X.XX EUR)" so user always sees real EUR amount.
 * NEVER use this amount for payment.
 */
export function formatDisplayPrice(eurAmount, displayCurrency) {
  if (!displayCurrency || displayCurrency === 'EUR') {
    return `€${eurAmount.toFixed(2)}`;
  }
  const rate = DISPLAY_RATES[displayCurrency] || 1;
  const displayAmount = eurAmount * rate;
  const decimals = CURRENCY_DECIMALS[displayCurrency] || 2;
  const symbol = CURRENCY_SYMBOLS[displayCurrency] || displayCurrency;
  const formatted = decimals === 0
    ? Math.round(displayAmount).toLocaleString()
    : displayAmount.toFixed(decimals);

  // Always show EUR equivalent so user knows what they actually pay
  return `${symbol}${formatted}`;
}

/**
 * Format for checkout — always EUR, always explicit.
 * This is the only format used in payment flows.
 */
export function formatEurCheckout(eurAmount) {
  if (typeof eurAmount !== 'number' || isNaN(eurAmount)) return '€0.00';
  return `€${eurAmount.toFixed(2)}`;
}

/**
 * Detect country from browser locale/timezone.
 * Returns a country code from COUNTRY_CONFIG.
 */
export function detectCountry() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const tzMap = {
      'Europe/Berlin':     'DE',
      'Europe/Vienna':     'AT',
      'Europe/Bratislava': 'SK',
      'Europe/Budapest':   'HU',
      'Europe/Bucharest':  'RO',
      'Europe/Warsaw':     'PL',
      'Europe/Amsterdam':  'NL',
      'Europe/Zagreb':     'HR',
    };
    if (tzMap[tz] && COUNTRY_CONFIG[tzMap[tz]]) return tzMap[tz];
  } catch (_) {}
  return 'DE'; // Default to Germany
}

/**
 * Get shipping cost in EUR for an order.
 * Returns 0 if order qualifies for free shipping.
 */
export function getShippingEur(subtotalEur, country) {
  const config = COUNTRY_CONFIG[country] || COUNTRY_CONFIG.DE;
  return subtotalEur >= config.shipping_free_eur ? 0 : 4.90;
}

/**
 * Calculate loyalty points earned from an EUR purchase.
 * 1 point per €1 spent (rounded down).
 */
export function calcLoyaltyPoints(eurAmount) {
  return Math.floor(eurAmount);
}

/**
 * Get loyalty tier info.
 */
export function getLoyaltyTier(points) {
  if (points >= 2000) return { tier: 'elite',   label: '🌌 Cosmiq Elite', discount: 0.15, nextTier: null,   nextAt: null };
  if (points >= 500)  return { tier: 'crystal', label: '💎 Crystal',      discount: 0.10, nextTier: 'elite', nextAt: 2000 };
  return              { tier: 'stone',   label: '🪨 Stone',        discount: 0.05, nextTier: 'crystal', nextAt: 500 };
}